import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { arrowRightIcon, chevronDownIcon } from '../../../../../icons';
import { CheckoutFlowService } from '../../../../checkout/checkout-flow.service';
import { CheckoutService } from '../../../../checkout/checkout.service';
import { SettingsService } from '../../../../core/settings.service';
import { Cms } from '@impact/data';

export enum IInsuranceType {
    nonSelected = 'Ukendt',
}
@Component({
    selector: 'app-checkout-step-insurance',
    template: `
        <section class="flow-step">
            <section class="flow-step__header">
                <h1 class="h4">
                    {{ 'checkout.insurance_step____headline' | translate }}
                </h1>
                <p>{{ 'checkout.insurance_step____description' | translate }}</p>
            </section>
            <section class="flow-step__content" [ngSwitch]="isReady">
                <ng-container *ngSwitchCase="true">
                    <app-fade-in>
                        <ng-container *ngFor="let insuranceCompany of settingsData?.insuranceCompanies">
                            <app-insurance-spot
                                *ngIf="insuranceCompany.promotedCompany"
                                [insurance]="insuranceCompany"
                                [activeCompany]="activeCompany"
                                (getClickedCompanyName)="handleSelect($event)"
                            ></app-insurance-spot>
                        </ng-container>
                    </app-fade-in>

                    <div *ngIf="unPromotedCompanies.length"
                        class="insurance-spot__toggle-more"
                        (click)="toggleUnpromotedInsurnces()"
                        [ngClass]="{ 'insurance-spot__toggle-more--upside-down': showUnpromotedInsurances }"
                    >
                        <p class="insurance-spot__toggle-more-text" *ngIf="showUnpromotedInsurances">
                            {{ 'checkout.insurance_step____toggle_less_insurance_companies' | translate }}
                        </p>
                        <p class="insurance-spot__toggle-more-text" *ngIf="!showUnpromotedInsurances">
                            {{ 'checkout.insurance_step____toggle_more_insurance_companies' | translate }}
                        </p>
                        <i aria-hidden="true" class="button__icon">${chevronDownIcon}</i>
                    </div>

                    <ng-container *ngIf="showUnpromotedInsurances && unPromotedCompanies.length">
                        <app-fade-in>
                            <div class="unpromoted-insurance-list">
                                <ng-container *ngFor="let insuranceCompany of settingsData?.insuranceCompanies">
                                    <app-insurance-spot-small
                                        *ngIf="!insuranceCompany.promotedCompany"
                                        [insurance]="insuranceCompany"
                                        [activeCompany]="activeCompany"
                                        (getClickedCompanyName)="handleSelect($event)"
                                    ></app-insurance-spot-small>
                                </ng-container>
                            </div>
                        </app-fade-in>
                    </ng-container>

                    <div class="center grid-spacing-normal">
                        <app-button
                            *ngIf="!(checkoutFlowService.atFlowStart$ | async)"
                            buttonClass="button--tertiary button--has-spacing button--has-bottom-spacing"
                            [title]="'checkout.button____go_back_to_previous_step' | translate"
                            (handleClick)="checkoutFlowService.prevStep()"
                        ></app-button>
                        <app-button
                            (handleClick)="handleSubmit()"
                            [buttonClass]="activeCompany ? 'button--primary' : 'button--tertiary'"
                            [loading]="!!(checkoutService.isCheckoutLoading$ | async)"
                            [title]="
                                activeCompany
                                    ? ('checkout.button____choose_and_proceed' | translate)
                                    : ('checkout.insurance_step____button_option____proceed_without_insurace' | translate)
                            "
                        >
                            <i aria-hidden="true" class="button__icon">${arrowRightIcon}</i>
                        </app-button>
                    </div>

                    <div class="grid-spacing-normal center">
                        <p>
                            {{ 'checkout.insurance_step____disclaimer_text' | translate }}
                        </p>
                    </div>
                </ng-container>
                <app-component-loader *ngSwitchCase="false"></app-component-loader>
            </section>
        </section>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutStepInsuranceComponent implements OnInit, OnDestroy {
    activeCompany?: string | null;
    showUnpromotedInsurances = false;
    unPromotedCompanies: Cms.InsuranceCompany[] = [];
    settingsData: Cms.SettingsDto | undefined;
    checkoutData: any;
    isReady = false;
    private readonly unsubscribe = new Subject<void>();

    constructor(
        public checkoutService: CheckoutService,
        readonly settingsService: SettingsService,
        public checkoutFlowService: CheckoutFlowService,
        private cdr: ChangeDetectorRef
    ) {}
    ngOnInit() {
        const settings$ = this.settingsService.get();
        const checkoutData$ = this.checkoutService.getCheckoutData();
        const connectStream$ = combineLatest([settings$, checkoutData$]);
        
        connectStream$.pipe(takeUntil(this.unsubscribe)).subscribe(([settings, checkoutData]) => {
            this.settingsData = settings;
            this.checkoutData = checkoutData;
            this.activeCompany = this.checkoutData?.insurance?.company;

            this.unPromotedCompanies = settings.insuranceCompanies.filter((i) => !i.promotedCompany);

            this.showUnpromotedInsurances =
                settings.insuranceCompanies.some(
                    (i) =>
                        !!i.promotedCompany &&
                        this.activeCompany === i.companyName
                );

            this.isReady = true;
            this.cdr.markForCheck();
        });
    }

    toggleUnpromotedInsurnces() {
        this.showUnpromotedInsurances = !this.showUnpromotedInsurances;
    }

    handleSelect(companyName?: string) {
        this.activeCompany = this.activeCompany === companyName ? null : companyName;
    }

    handleSubmit() {
        this.checkoutService.setInsurance(this.activeCompany || IInsuranceType.nonSelected);
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
